import React, { useMemo } from "react";
import { CriteriaBreakdown } from "types/assessmentTypes";
import { Company } from "types/companyTypes";
import { calculateCompleteStrategy } from "../helper/strategyCalculations";
import { industryWieghts } from "variables/industryWieghts";
import { useTranslation } from "react-i18next";
import {
  HiOutlineChartBar,
  HiOutlineDocumentText,
  HiOutlineLightBulb,
  HiOutlineShieldCheck,
} from "react-icons/hi2";
import { HiOutlineClipboardCheck } from "react-icons/hi";
import { useScope } from "hooks/useScope";

export type StrategySection =
  | "header"
  | "scores"
  | "levels"
  | "rules"
  | "primary"
  | "final";

const DEFAULT_SECTIONS: StrategySection[] = [
  "header",
  "scores",
  "levels",
  "rules",
  "primary",
  "final",
];

interface StrategyReportProps {
  criteriaBreakdown: CriteriaBreakdown[];
  company: Company;
  sections?: StrategySection[];
  pdfMode?: boolean;
}

const StrategyReport: React.FC<StrategyReportProps> = ({
  criteriaBreakdown,
  company,
  sections = DEFAULT_SECTIONS,
  pdfMode = false,
}) => {
  const { t } = useTranslation();
  const visible = useMemo(() => new Set(sections), [sections]);
  const has = (s: StrategySection) => visible.has(s);
  const scope = useScope();

  const strategyResult = useMemo(() => {
    if (!criteriaBreakdown || !company?.industrySector?.key) {
      return null;
    }

    try {
      return calculateCompleteStrategy(
        criteriaBreakdown,
        industryWieghts,
        company.industrySector.key
      );
    } catch (error) {
      console.error("Error calculating strategy:", error);
      return null;
    }
  }, [criteriaBreakdown, company]);

  if (!strategyResult) {
    return (
      <div className="p-6 text-center text-gray-500 dark:text-gray-400">
        {t("strategy.noData")}
      </div>
    );
  }

  const {
    scores,
    levels,
    allRules,
    primaryStrategy,
    secondaryStrategy,
    isTransitional,
    transitions,
    strategyCounts,
    finalStrategy,
    boundaryEvaluation,
  } = strategyResult;

  // Level color mapping
  const getLevelColor = (code: string) => {
    switch (code) {
      case "L":
        return "bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-300";
      case "T1":
      case "T2":
        return "bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-300";
      case "M":
        return "bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300";
      case "H":
        return "bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-300";
      default:
        return "bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-900/30 dark:text-gray-300";
    }
  };

  // Strategy badge color mapping
  const getStrategyBadgeColor = (strategy: string) => {
    switch (strategy) {
      case "Growth":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
      case "Maintain":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300";
      case "AnalyzeReview":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300";
      case "HarvestExit":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300";
    }
  };

  // Stability badge color mapping
  const getStabilityBadge = (stability: string) => {
    switch (stability) {
      case "VeryHigh":
        return "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300";
      case "High":
        return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300";
      case "Medium":
        return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300";
      case "Low":
        return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300";
      default:
        return "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300";
    }
  };

  return (
    <div className={pdfMode ? "w-full space-y-3" : "space-y-6 p-2"}>
      {/* Header */}
      {has("header") && (
        <div className="border-b border-gray-200 pb-4 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            📊 {t("strategy.title")}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t("strategy.industry")}:{" "}
            {t(`industrySectors.${company.industrySector?.key}`)}
          </p>
        </div>
      )}
      {/* ===== Step 1: IA, CP, ER Scores ===== */}
      {has("scores") && (
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-navy-800">
          <div className="mb-2 flex items-center gap-2">
            <HiOutlineChartBar className="h-5 w-5 text-blue-500" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t("strategy.step1")}
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {[
              {
                key: "IA",
                label: t("strategy.ia"),
                value: scores.IA,
                level: levels.IA,
              },
              {
                key: "CP",
                label: t("strategy.cp"),
                value: scores.CP,
                level: levels.CP,
              },
              {
                key: "ER",
                label: t("strategy.er"),
                value: scores.ER,
                level: levels.ER,
              },
            ].map((item) => (
              <div
                key={item.key}
                className="rounded-lg border border-gray-100 p-4 dark:border-gray-700"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-400">
                    {item.label}
                  </span>
                  <span
                    className={`rounded-full border px-3 py-1 text-sm font-bold ${getLevelColor(
                      item.level.code
                    )}`}
                  >
                    {item.level.code}
                  </span>
                </div>
                <div className="mt-2">
                  <span className="text-3xl font-bold text-gray-900 dark:text-white">
                    {item.value.toFixed(2)}
                  </span>
                  <span className="ml-1 text-sm text-gray-700">/ 100</span>
                </div>
                <div className="mt-2 h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700">
                  <div
                    className={`h-2 rounded-full transition-all duration-500 ${
                      item.level.code === "H"
                        ? "bg-green-500"
                        : item.level.code === "M"
                        ? "bg-blue-500"
                        : item.level.code === "T1" || item.level.code === "T2"
                        ? "bg-yellow-500"
                        : "bg-red-500"
                    }`}
                    style={{ width: `${Math.min(item.value, 100)}%` }}
                  />
                </div>
                <div className="mt-1 text-xs text-gray-700">
                  {t("strategy.range")}: {item.level.min} - {item.level.max}
                  {item.level.transition &&
                    ` 🔄 (${t("strategy.transitional")})`}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== Step 2: Strategic Levels ===== */}
      {has("levels") && scope === "admin" && (
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-navy-800">
          <div className="mb-4 flex items-center gap-2">
            <HiOutlineClipboardCheck className="h-5 w-5 text-purple-500" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t("strategy.step2")}
            </h3>
            {isTransitional && (
              <span className="rounded-full bg-yellow-100 px-3 py-1 text-xs font-medium text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300">
                ⚠️ {transitions.length} {t("strategy.inTransition")}
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-900/50">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                IA
              </span>
              <p className="font-bold text-gray-900 dark:text-white">
                {levels.IA.code} -{" "}
                {t(`strategy.levels.${levels.IA.code}`, levels.IA.title)}
              </p>
            </div>
            <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-900/50">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                CP
              </span>
              <p className="font-bold text-gray-900 dark:text-white">
                {levels.CP.code} -{" "}
                {t(`strategy.levels.${levels.CP.code}`, levels.CP.title)}
              </p>
            </div>
            <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-900/50">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                ER
              </span>
              <p className="font-bold text-gray-900 dark:text-white">
                {levels.ER.code} -{" "}
                {t(`strategy.levels.${levels.ER.code}`, levels.ER.title)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ===== Step 3: Rules Evaluated ===== */}
      {has("rules") && scope === "admin" && (
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-navy-800">
          <div className="mb-4 flex items-center gap-2">
            <HiOutlineDocumentText className="h-5 w-5 text-indigo-500" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t("strategy.step3")} ({allRules.length} {t("strategy.items")})
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-400">
                    #
                  </th>
                  <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-400">
                    IA
                  </th>
                  <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-400">
                    CP
                  </th>
                  <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-400">
                    ER
                  </th>
                  <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-400">
                    {t("strategy.rule")}
                  </th>
                  <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-400">
                    {t("strategy.direction")}
                  </th>
                  <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-400">
                    {t("strategy.confidence")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {allRules.map((rule, index) => (
                  <tr
                    key={index}
                    className="border-b border-gray-100 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-navy-900/50"
                  >
                    <td className="px-3 py-2 text-gray-500">{index + 1}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`rounded px-2 py-1 text-xs font-medium ${getLevelColor(
                          rule.IA
                        )}`}
                      >
                        {rule.IA}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`rounded px-2 py-1 text-xs font-medium ${getLevelColor(
                          rule.CP
                        )}`}
                      >
                        {rule.CP}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`rounded px-2 py-1 text-xs font-medium ${getLevelColor(
                          rule.ER
                        )}`}
                      >
                        {rule.ER}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-600 dark:text-gray-400">
                      {rule.RULE}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`rounded px-2 py-1 text-xs font-medium ${getStrategyBadgeColor(
                          rule.StrategicDirection
                        )}`}
                      >
                        {t(
                          `strategy.strategyTypes.${rule.StrategicDirection}`,
                          rule.StrategicDirection
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                      {t(
                        `strategy.stabilityLevels.${rule.Confidence}`,
                        rule.Confidence
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Rule Descriptions */}
          <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-2">
            {allRules.map((rule, index) => (
              <div
                key={index}
                className="rounded-lg border border-gray-100 p-3 text-xs dark:border-gray-700"
              >
                <span className="font-mono font-bold text-gray-700 dark:text-gray-300">
                  {rule.RULE}:
                </span>
                <span className="text-gray-600 dark:text-gray-400">
                  {" "}
                  {t(
                    `strategy.strategyDescriptions.${rule.description}`,
                    rule.description
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== Step 4: Primary & Secondary Strategies ===== */}
      {has("primary") && (
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-navy-800">
          <div className="mb-4 flex flex-col flex-wrap gap-3 md:flex-row md:items-start md:justify-between">
            <div className="flex-2">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <HiOutlineLightBulb className="h-5 w-5 text-amber-500" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t("strategy.step4")}
                </h3>
                {isTransitional && (
                  <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                    🔄 {t("strategy.transitionalMode")}
                  </span>
                )}
              </div>

              {/* Strategy Distribution */}
              <div className="flex flex-wrap gap-2">
                {Object.entries(strategyCounts).map(([strategy, count]) => (
                  <span
                    key={strategy}
                    className={`rounded-full px-3 py-1 text-xs font-medium ${getStrategyBadgeColor(
                      strategy
                    )}`}
                  >
                    {t(`strategy.strategyTypes.${strategy}`, strategy)}: {count}{" "}
                    {t("strategy.times")}
                  </span>
                ))}
              </div>
            </div>

            {secondaryStrategy &&
              strategyCounts[primaryStrategy] ===
                strategyCounts[secondaryStrategy] &&
              transitions.length > 0 && (
                <div className="flex-1 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
                  <div className="flex items-start gap-2">
                    <span className="text-lg text-amber-500">⚠️</span>
                    <div>
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                        {t("strategy.equalCountWarning")}
                      </p>
                      <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                        {t("strategy.equalCountDetail", {
                          transitions: transitions
                            .map(
                              (i) =>
                                `${t(
                                  `strategy.${i.toLocaleLowerCase()}`
                                )} (${i})`
                            )
                            .join("، "),
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              )}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* Primary */}
            <div className="rounded-lg border-2 border-green-200 bg-green-50 p-2 dark:border-green-800 dark:bg-green-900/20">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-green-900 dark:text-green-300">
                  ⭐ {t("strategy.primary")}
                </span>
                <span className="rounded-full bg-green-200 px-2 py-0.5 text-xs font-bold text-green-800 dark:bg-green-800 dark:text-green-200">
                  {strategyCounts[primaryStrategy] || 0} {t("strategy.times")}
                </span>
              </div>
              <p className="mt-1 text-lg font-bold text-gray-900 dark:text-white">
                {t(
                  `strategy.strategyTypes.${primaryStrategy}`,
                  primaryStrategy
                )}
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-400">
                {t(`strategy.strategyDescriptionsShort.${primaryStrategy}`, "")}
              </p>
            </div>

            {/* Secondary */}
            {secondaryStrategy ? (
              <div className="rounded-lg border-2 border-blue-200 bg-blue-50 p-2 dark:border-blue-800 dark:bg-blue-900/20">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-blue-900 dark:text-blue-300">
                    🔄 {t("strategy.secondary")}
                  </span>
                  <span className="rounded-full bg-blue-200 px-2 py-0.5 text-xs font-bold text-blue-800 dark:bg-blue-800 dark:text-blue-200">
                    {strategyCounts[secondaryStrategy] || 0}{" "}
                    {t("strategy.times")}
                  </span>
                </div>
                <p className="mt-1 text-lg font-bold text-gray-900 dark:text-white">
                  {t(
                    `strategy.strategyTypes.${secondaryStrategy}`,
                    secondaryStrategy
                  )}
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-400">
                  {t(
                    `strategy.strategyDescriptionsShort.${secondaryStrategy}`,
                    ""
                  )}
                </p>
              </div>
            ) : (
              <div className="rounded-lg border-2 border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/20">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t("strategy.noSecondary")}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===== Step 5: Final Strategy ===== */}
      {has("final") && finalStrategy && (
        <div className="relative overflow-hidden rounded-2xl border-0 bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 p-4 shadow-xl dark:from-purple-900/20 dark:via-indigo-900/20 dark:to-blue-900/20">
          {/* Decorative background elements */}
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-purple-200/30 blur-3xl dark:bg-purple-500/10" />
          <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-indigo-200/30 blur-3xl dark:bg-indigo-500/10" />
          <div className="absolute right-1/2 top-1/2 h-96 w-96 -translate-y-1/2 translate-x-1/2 rounded-full bg-blue-200/20 blur-3xl dark:bg-blue-500/5" />

          <div className="relative">
            {/* Header with gradient border - Combined with Order, Stability, Boundary */}
            <div className="relative mb-5">
              <div className="absolute -inset-0.5 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 opacity-30 blur-sm" />
              <div className="relative flex flex-wrap items-center justify-between gap-3 rounded-xl bg-white/80 px-5 py-3 backdrop-blur-sm dark:bg-navy-800/80">
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-gradient-to-r from-purple-500 to-indigo-500 p-2 shadow-lg shadow-purple-500/25">
                    <HiOutlineShieldCheck className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    {t("strategy.step5")}
                  </h3>
                </div>

                {/* Order, Stability, Boundary in one row inside header */}
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-400">
                      {t("strategy.order")}:
                    </span>
                    <span className="rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 px-3 py-1 text-sm font-bold text-white shadow-lg shadow-purple-500/25">
                      #{finalStrategy.order}
                    </span>
                  </div>

                  <div className="hidden h-6 w-px bg-gray-300 dark:bg-gray-600" />

                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-400">
                      {t("strategy.strategicStability")}:
                    </span>
                    <span
                      className={`rounded-lg px-3 py-1 text-sm font-bold ${getStabilityBadge(
                        finalStrategy.StrategicStability
                      )}`}
                    >
                      {t(
                        `strategy.stabilityLevels.${finalStrategy.StrategicStability}`,
                        finalStrategy.StrategicStability
                      )}
                    </span>
                  </div>

                  <div className="hidden h-6 w-px bg-gray-300 dark:bg-gray-600" />

                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-400">
                      {t("strategy.boundaryStatus")}:
                    </span>
                    <span className="rounded-lg bg-amber-100 px-3 py-1 text-sm font-bold text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                      {t(
                        `strategy.boundaryStatuses.${boundaryEvaluation.DecisionStatus}`,
                        boundaryEvaluation.DecisionStatus
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="space-y-4 rounded-2xl bg-white/60 p-5 backdrop-blur-sm dark:bg-navy-800/60 md:p-6">
              {/* Status */}
              <div className="flex items-start gap-3">
                <div className="mt-0.5 rounded-full bg-purple-100 p-1.5 dark:bg-purple-900/30">
                  <svg
                    className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-xs font-semibold uppercase tracking-wider text-purple-600 dark:text-purple-400">
                    {t("strategy.status")}
                  </p>
                  <p className="mt-0.5 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                    {t(`strategy.strategyModel.${finalStrategy.order}.status`)}
                  </p>
                </div>
              </div>

              {/* Divider with gradient */}
              <div className="border-t border-gray-200/50 dark:border-gray-700/50" />

              {/* Interpretation */}
              <div className="flex items-start gap-3">
                <div className="mt-0.5 rounded-full bg-blue-100 p-1.5 dark:bg-blue-900/30">
                  <svg
                    className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-xs font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                    {t("strategy.interpretation")}
                  </p>
                  <p className="mt-0.5 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                    {t(
                      `strategy.strategyModel.${finalStrategy.order}.interpretation`
                    )}
                  </p>
                </div>
              </div>

              {/* Divider with gradient */}
              <div className="border-t border-gray-200/50 dark:border-gray-700/50" />

              {/* Direction */}
              <div className="flex items-start gap-3">
                <div className="mt-0.5 rounded-full bg-cyan-100 p-1.5 dark:bg-cyan-900/30">
                  <svg
                    className="h-3.5 w-3.5 text-cyan-600 dark:text-cyan-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-xs font-semibold uppercase tracking-wider text-cyan-600 dark:text-cyan-400">
                    {t("strategy.direction")}
                  </p>
                  <p className="mt-0.5 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                    {t(
                      `strategy.strategyModel.${finalStrategy.order}.direction`
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StrategyReport;
